import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { roleApi, isApiError } from '../../api';
import { queryKeys, cacheUtils, queryClient } from '../query-client';
import { useNotifications } from '../notifications';
import { useProtectedQuery } from '../use-protected-query';
import type { 
  Role, 
  RoleSearchParams, 
  CreateRoleRequest, 
  UpdateRoleRequest,
  PaginatedResponse,
  RoleUsageInfo
} from '../../types';

// Hook for fetching paginated roles list
export const useRoles = (params?: RoleSearchParams) => {
  return useProtectedQuery({
    queryKey: queryKeys.roles.list(params),
    queryFn: () => roleApi.getRoles(params),
    staleTime: 5 * 60 * 1000, // 5 minutes for roles (less frequent changes)
    placeholderData: (previousData: PaginatedResponse<Role> | undefined) => previousData, // Keep previous data while loading
    // Enable background refetching for better UX
    refetchOnWindowFocus: true,
    // Prefetch next page for pagination
    select: (data: PaginatedResponse<Role>) => {
      // If we have a next page, prefetch it
      if (data && params?.page && data.pages > params.page) {
        const nextPageParams = { ...params, page: params.page + 1 };
        setTimeout(() => {
          queryClient.prefetchQuery({
            queryKey: queryKeys.roles.list(nextPageParams),
            queryFn: () => roleApi.getRoles(nextPageParams),
            staleTime: 5 * 60 * 1000,
          });
        }, 100);
      }
      return data;
    },
  });
};

// Hook for fetching a single role
export const useRole = (id: string, enabled: boolean = true) => {
  return useProtectedQuery({
    queryKey: queryKeys.roles.detail(id),
    queryFn: () => roleApi.getRole(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes for individual roles
  });
};

// Hook for creating a new role
export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => roleApi.createRole(data),
    onMutate: async (newRole) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.roles.lists() });
      
      // Snapshot the previous value
      const previousRoles = queryClient.getQueriesData({ queryKey: queryKeys.roles.lists() });
      
      // Optimistically update to the new value
      queryClient.setQueriesData(
        { queryKey: queryKeys.roles.lists() },
        (old: PaginatedResponse<Role> | undefined) => {
          if (!old) return old;
          
          // Create optimistic role object
          const optimisticRole: Role = {
            id: `temp-${Date.now()}`, // Temporary ID
            name: newRole.name,
            description: newRole.description,
            permissions: newRole.permissions,
            serviceName: newRole.serviceName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          
          return {
            ...old,
            items: [optimisticRole, ...old.items],
            total: old.total + 1,
          };
        }
      );
      
      return { previousRoles };
    },
    onError: (error, _newRole, context) => {
      // Rollback optimistic update on error
      if (context?.previousRoles) {
        context.previousRoles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      notifications.operationError('roleCreated', error);
    },
    onSuccess: (createdRole) => {
      // Replace optimistic update with real data
      queryClient.setQueriesData(
        { queryKey: queryKeys.roles.lists() },
        (old: PaginatedResponse<Role> | undefined) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.map(role => 
              role.id.startsWith('temp-') ? createdRole : role
            ),
          };
        }
      );
      
      // Cache the new role
      queryClient.setQueryData(queryKeys.roles.detail(createdRole.id), createdRole);
      
      notifications.operationSuccess('roleCreated');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      cacheUtils.invalidateRoles();
    },
  });
};

// Hook for updating a role
export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      roleApi.updateRole(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.roles.detail(id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.roles.lists() });
      
      // Snapshot the previous value
      const previousRole = queryClient.getQueryData(queryKeys.roles.detail(id));
      const previousRoles = queryClient.getQueriesData({ queryKey: queryKeys.roles.lists() });
      
      // Optimistically update the role
      if (previousRole) {
        const optimisticRole = { ...previousRole as Role, ...data };
        
        queryClient.setQueryData(queryKeys.roles.detail(id), optimisticRole);
        cacheUtils.updateRoleInCache(id, (old: Role) => ({ ...old, ...data }));
      }
      
      return { previousRole, previousRoles };
    },
    onError: (error, { id }, context) => {
      // Rollback optimistic updates on error
      if (context?.previousRole) {
        queryClient.setQueryData(queryKeys.roles.detail(id), context.previousRole);
      }
      if (context?.previousRoles) {
        context.previousRoles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      notifications.operationError('roleUpdated', error);
    },
    onSuccess: (updatedRole, { id }) => {
      // Update cache with real data
      queryClient.setQueryData(queryKeys.roles.detail(id), updatedRole);
      cacheUtils.updateRoleInCache(id, () => updatedRole);
      
      notifications.operationSuccess('roleUpdated');
    },
  });
};

// Hook for checking role usage
export const useRoleUsage = (id: string, enabled: boolean = true) => {
  return useProtectedQuery<RoleUsageInfo>({
    queryKey: queryKeys.roles.usage(id),
    queryFn: () => roleApi.checkRoleUsage(id),
    enabled: enabled && !!id,
    staleTime: 30 * 1000, // 30 seconds - usage info changes frequently
  });
};

// Hook for deleting a role
export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notifications = useNotifications(t);
  
  return useMutation({
    mutationFn: (id: string) => roleApi.deleteRole(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.roles.detail(id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.roles.lists() });
      
      // Snapshot the previous values
      const previousRole = queryClient.getQueryData(queryKeys.roles.detail(id));
      const previousRoles = queryClient.getQueriesData({ queryKey: queryKeys.roles.lists() });
      
      // Optimistically remove the role
      cacheUtils.removeRoleFromCache(id);
      
      return { previousRole, previousRoles };
    },
    onError: (error, id, context) => {
      // Rollback optimistic updates on error
      if (context?.previousRole) {
        queryClient.setQueryData(queryKeys.roles.detail(id), context.previousRole);
      }
      if (context?.previousRoles) {
        context.previousRoles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      
      // Handle specific error for role in use with enhanced suggestions
      if (isApiError(error)) {
        if (error.message.includes('assigned users') || 
            error.message.includes('Cannot delete role with assigned users')) {
          notifications.error(error, 'Role deletion', {
            description: 'Remove all user assignments first, then try deleting the role again.',
          });
        } else {
          notifications.operationError('roleDeleted', error);
        }
      } else {
        notifications.operationError('roleDeleted', error);
      }
    },
    onSuccess: () => {
      notifications.operationSuccess('roleDeleted');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      cacheUtils.invalidateRoles();
      // Also invalidate users since they might have had this role
      cacheUtils.invalidateUsers();
    },
  });
};